import React, { useEffect, useMemo, useState } from 'react';
import { Modal, TextInput } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faLock, faCalendarDays, faIdCard } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { ChargeManualCreditRequest } from 'shared-models/browser';
import classes from './manual-credit-dialog.module.scss';

type ManualCreditDialogProps = {
  isOpen: boolean;
  terminalLabel: string;
  amountLabel: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (card: Omit<ChargeManualCreditRequest, 'terminalId' | 'pinpadId'>) => void;
};

// Luhn check digit — mirrors the backend DTO so an obviously-mistyped PAN is caught before the POST.
function passesLuhn(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let value = digits.charCodeAt(i) - 48;
    if (double) {
      value *= 2;
      if (value > 9) {
        value -= 9;
      }
    }
    sum += value;
    double = !double;
  }
  return sum % 10 === 0;
}

// MMYY is a valid month not in the past — mirrors the backend not-expired refinement.
function isNotExpired(mmyy: string): boolean {
  if (!/^(0[1-9]|1[0-2])\d{2}$/.test(mmyy)) {
    return false;
  }
  const month = Number(mmyy.slice(0, 2));
  const year = 2000 + Number(mmyy.slice(2, 4));
  const now = new Date();
  return new Date(year, month, 1) > new Date(now.getFullYear(), now.getMonth(), 1);
}

// Group typed digits into 4s and pad the rest with bullets so the card preview always shows full slots.
function formatCardDisplay(digits: string): string {
  const slots = Math.max(16, Math.ceil(Math.max(digits.length, 1) / 4) * 4);
  return digits.padEnd(slots, '•').replace(/(.{4})(?=.)/g, '$1 ');
}

export const ManualCreditDialog: React.FC<ManualCreditDialogProps> = ({
  isOpen,
  amountLabel,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const [cardNumber, setCardNumber] = useState('');
  const [expDateMMYY, setExpDateMMYY] = useState('');
  const [cvv, setCvv] = useState('');
  const [idNumber, setIdNumber] = useState('');

  // Card data lives only while the popup is mounted; clear it whenever the popup (re)opens or closes.
  useEffect(() => {
    setCardNumber('');
    setExpDateMMYY('');
    setCvv('');
    setIdNumber('');
  }, [isOpen]);

  const normalizedCard = cardNumber.replace(/[\s-]/g, '');
  const normalizedExp = expDateMMYY.replace(/[\s/]/g, '');

  const cardValid = /^\d{13,19}$/.test(normalizedCard) && passesLuhn(normalizedCard);
  const expValid = isNotExpired(normalizedExp);
  const cvvValid = /^\d{3,4}$/.test(cvv.trim());
  const idValid = idNumber.trim() === '' || /^\d{1,15}$/.test(idNumber.trim());
  const canSubmit = cardValid && expValid && cvvValid && idValid && !isSubmitting;

  const cardError = useMemo(() => {
    if (cardNumber === '') {
      return null;
    }
    if (!/^\d{13,19}$/.test(normalizedCard)) {
      return 'מספר כרטיס לא תקין';
    }
    return passesLuhn(normalizedCard) ? null : 'מספר כרטיס לא תקין';
  }, [cardNumber, normalizedCard]);

  const expError = expDateMMYY !== '' && !expValid ? 'תוקף לא תקין' : null;
  const cvvError = cvv !== '' && !cvvValid ? 'CVV לא תקין' : null;
  const idError = idNumber !== '' && !idValid ? 'מספר זהות לא תקין' : null;

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }
    onSubmit({
      cardNumber: normalizedCard,
      expDateMMYY: normalizedExp,
      cvv: cvv.trim(),
      idNumber: idNumber.trim() === '' ? null : idNumber.trim(),
    });
  };

  const expDisplay =
    normalizedExp.length === 0
      ? 'MM/YY'
      : `${normalizedExp.slice(0, 2)}${normalizedExp.length > 2 ? '/' : ''}${normalizedExp.slice(2, 4)}`;
  const cvvDisplay = cvv.trim().length > 0 ? '•'.repeat(Math.min(cvv.trim().length, 4)) : '•••';
  const progress = [cardValid, cvvValid, expValid, idNumber.trim() !== ''];

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      centered
      dir="rtl"
      radius="lg"
      size={440}
      closeButtonProps={{ 'data-testid': 'manual-credit-close' } as Record<string, string>}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: '#2f9e6f',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FontAwesomeIcon icon={faCreditCard as IconProp} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span style={{ fontWeight: 700 }}>אשראי ידני</span>
            <span style={{ fontSize: 12, color: '#2f9e6f' }}>הזן את פרטי כרטיס האשראי</span>
          </div>
        </div>
      }
    >
      <div data-testid="manual-credit-dialog" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          className={classes.creditCardGradient}
          style={{
            position: 'relative',
            borderRadius: 18,
            padding: 22,
            color: '#fff',
            boxShadow: '0 12px 28px rgba(14, 26, 51, 0.4)',
            minHeight: 184,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            fontFamily: 'monospace',
            direction: 'ltr',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div
              style={{
                width: 48,
                height: 36,
                borderRadius: 8,
                background: 'linear-gradient(135deg, #f7d680, #d6a23a)',
                border: '2px solid #c8881d',
              }}
            />
          </div>

          <div style={{ fontSize: 23, letterSpacing: 3, fontWeight: 700 }}>{formatCardDisplay(normalizedCard)}</div>

          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ position: 'relative', width: 46, height: 28 }}>
                <span
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#eb001b',
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    left: 16,
                    top: 0,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#f79e1b',
                    mixBlendMode: 'screen',
                  }}
                />
              </div>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, opacity: 0.8 }}>
                <FontAwesomeIcon icon={faLock as IconProp} /> מאובטח
              </span>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, letterSpacing: 1, opacity: 0.6 }}>CVV</div>
              <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 2 }}>{cvvDisplay}</div>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, letterSpacing: 1, opacity: 0.6 }}>תוקף</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{expDisplay}</div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          {progress.map((done, index) => (
            <div
              key={index}
              style={{ flex: 1, height: 4, borderRadius: 2, background: done ? '#2f9e6f' : '#d7dde5' }}
            />
          ))}
        </div>

        <TextInput
          label="מספר כרטיס"
          data-testid="manual-credit-card-number"
          inputMode="numeric"
          leftSection={<FontAwesomeIcon icon={faCreditCard as IconProp} />}
          value={cardNumber}
          error={cardError}
          onChange={(event) => setCardNumber(event.currentTarget.value)}
        />
        <div style={{ display: 'flex', gap: 12 }}>
          <TextInput
            label="תוקף (MMYY)"
            data-testid="manual-credit-exp"
            inputMode="numeric"
            placeholder="MMYY"
            leftSection={<FontAwesomeIcon icon={faCalendarDays as IconProp} />}
            value={expDateMMYY}
            error={expError}
            onChange={(event) => setExpDateMMYY(event.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <TextInput
            label="CVV"
            data-testid="manual-credit-cvv"
            inputMode="numeric"
            leftSection={<FontAwesomeIcon icon={faLock as IconProp} />}
            value={cvv}
            error={cvvError}
            onChange={(event) => setCvv(event.currentTarget.value)}
            style={{ flex: 1 }}
          />
        </div>
        <TextInput
          label="תעודת זהות (לא חובה)"
          data-testid="manual-credit-id"
          inputMode="numeric"
          leftSection={<FontAwesomeIcon icon={faIdCard as IconProp} />}
          value={idNumber}
          error={idError}
          onChange={(event) => setIdNumber(event.currentTarget.value)}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
          <span style={{ color: '#6b7280' }}>סכום לחיוב</span>
          <span dir="ltr">{amountLabel}</span>
        </div>

        <div className={classes.buttonRow}>
          <button
            type="button"
            data-testid="manual-credit-cancel"
            className={`${classes.btnCommon} ${classes.btnCancel}`}
            onClick={onClose}
            disabled={isSubmitting}
          >
            ביטול
          </button>
          <button
            type="button"
            data-testid="manual-credit-submit"
            className={`${classes.btnCommon} ${classes.btnConfirm}`}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isSubmitting ? 'טוען...' : 'אישור'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
